import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import useCartStore from "../api/cart";
import {
  CONFIRM_LATER,
  CUSTOM_KINDS,
  HOLE_PLACES,
  MAX_COUNT,
  MAX_CUSTOM,
  MAX_HOLES,
  MAX_NOTE,
  METHODS,
  PER_HOLE,
  RESTORATION,
  RESTORATION_FROM,
  SOMEWHERE_ELSE,
  chosenCustoms,
  customsTotal,
  expandCounts,
  extraLabel,
  holeLocation,
  holeSizeLabel,
  holesTotal,
  holePrices,
  REKNIT,
  REKNIT_REMINDER,
  isCountable,
  missingForContinue,
  newCustom,
  newHole,
  offeredOn,
  priceOn,
  splitSweaterServices,
  sweaterBasket,
  whoEstimates,
} from "../utils/sweaterScreen";

const pathURL = process.env.REACT_APP_API_URL;

// One screen for a sweater at the counter, in place of plan, service and
// repair details. See utils/sweaterScreen.js.
function SweaterScreen() {
  const navigate = useNavigate();
  const { cartItems, updateCartItem } = useCartStore();

  const garment = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("selectedItems"));
    } catch {
      return null;
    }
  })();

  const { data, isLoading } = useQuery({
    queryKey: ["sweaterServices"],
    queryFn: async () => {
      const { data } = await axios.post(`${pathURL}service/getservicebyitem`, {
        text: "SWEATER",
      });
      return Array.isArray(data) ? data : data?.data;
    },
  });
  const catalogue = useMemo(() => splitSweaterServices(data), [data]);

  const [plan, setPlan] = useState("Standard");
  const [holes, setHoles] = useState([]);
  const [counts, setCounts] = useState({});
  const setCount = (id, n) =>
    setCounts((prev) => ({
      ...prev,
      [id]: Math.max(0, Math.min(MAX_COUNT, n)),
    }));
  const [charge, setCharge] = useState(PER_HOLE);
  const [restorationPrice, setRestorationPrice] = useState("");
  const [tried, setTried] = useState(false);
  const [notes, setNotes] = useState("");
  const [customs, setCustoms] = useState([]);
  const setCustom = (index, change) =>
    setCustoms((prev) =>
      prev.map((custom, i) =>
        i === index ? { ...custom, ...change } : custom,
      ),
    );

  useEffect(() => {
    if (!cartItems?.[0] || !garment?.id) navigate("/");
  }, []);

  const holeOptions = catalogue.holes.filter((s) => offeredOn(s, plan));
  const extraOptions = catalogue.extras.filter((s) => offeredOn(s, plan));
  const chosenHoles = holes
    .map((hole) => ({
      ...hole,
      location: holeLocation(hole),
      service: holeOptions[hole.size],
    }))
    .filter((hole) => hole.service);
  const chosenExtras = expandCounts(extraOptions, counts);
  // Each hole's price on its row: its size's price, darned or reknit.
  const rowPrices = holePrices(
    holes.map((hole) => ({ ...hole, service: holeOptions[hole.size] })),
    plan,
  );
  const perHoleTotal = holesTotal({
    holes: chosenHoles,
    plan,
    charge: PER_HOLE,
  });
  const total =
    holesTotal({ holes: chosenHoles, plan, charge, restorationPrice }) +
    chosenExtras.reduce((sum, s) => sum + priceOn(s, plan), 0) +
    customsTotal(customs);

  // The choice shows by itself from RESTORATION_FROM holes; below that it
  // stays per hole unless staff open it.
  const [choosing, setChoosing] = useState(false);
  const showCharge =
    chosenHoles.length >= RESTORATION_FROM || choosing || charge !== PER_HOLE;

  const missing = missingForContinue({
    holes: chosenHoles,
    extras: chosenExtras,
    charge,
    restorationPrice,
    customs,
  });

  const setHole = (index, change) =>
    setHoles((prev) =>
      prev.map((hole, i) => (i === index ? { ...hole, ...change } : hole)),
    );

  const firstGap = () => {
    const unplaced = chosenHoles.findIndex((hole) => !hole.location);
    if (unplaced !== -1) {
      return document.getElementById(
        holes[unplaced]?.place
          ? `hole-other-${unplaced}`
          : `hole-place-${unplaced}`,
      );
    }
    const unmethod = holes.findIndex(
      (hole) => holeOptions[hole.size] && !hole.method,
    );
    if (unmethod !== -1) {
      return document.querySelector(`#hole-method-${unmethod} button`);
    }
    if (charge === RESTORATION && !parseFloat(restorationPrice)) {
      return document.getElementById("restoration-price");
    }
    const custom = customs.findIndex(
      (c) =>
        (c.what.trim() || c.price) && (!c.what.trim() || !parseFloat(c.price)),
    );
    if (custom !== -1) {
      return document.getElementById(
        customs[custom].what.trim()
          ? `custom-price-${custom}`
          : `custom-what-${custom}`,
      );
    }
    return document.querySelector(".sweater-hole");
  };

  const handleContinue = () => {
    if (missing.length) {
      setTried(true);
      const gap = firstGap();
      gap?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (gap?.focus) gap.focus({ preventScroll: true });
      return;
    }
    const basket = sweaterBasket(cartItems[0], {
      garmentId: garment.id,
      plan,
      holes: chosenHoles,
      extras: chosenExtras,
      charge: chosenHoles.length ? charge : PER_HOLE,
      restorationPrice,
      notes,
      customs,
    });
    updateCartItem(cartItems[0].id, basket);
    sessionStorage.setItem("SelectedPlan", plan);
    navigate("/item-detail");
  };

  return (
    <section className="items-section same-bg-section sweater-page relative">
      <div className="container padding-x-all">
        <div className="items-box sec-com-padding flex-box justify-between sweater-screen">
          <div className="show-box mobile-none sweater-summary">
            <div className="show-box-list">
              <p className="show-box-para">This item</p>
              <h4 className="show-box-answer">SWEATER</h4>
            </div>
            <div className="show-box-list">
              <p className="show-box-para">Plan</p>
              <h4 className="show-box-answer">{plan}</h4>
            </div>
            <div className="show-box-list">
              <p className="show-box-para">Total</p>
              <h4 className="show-box-answer">${total}</h4>
            </div>
          </div>
          <div className="item-select-box">
            <h2>What does the sweater need?</h2>

            <div className="sweater-block">
              <h3 className="sweater-h">Standard or Premium</h3>
              <div className="sweater-plans" role="radiogroup">
                {["Standard", "Premium"].map((name) => (
                  <button
                    key={name}
                    type="button"
                    role="radio"
                    aria-checked={plan === name}
                    className={
                      plan === name ? "sweater-plan on" : "sweater-plan"
                    }
                    onClick={() => setPlan(name)}
                  >
                    <b>FXRY {name}</b>
                    <span>
                      {name === "Standard"
                        ? "Top-notch quality for your everyday essentials, from our team of skilled fixers."
                        : "Our most seasoned fixers, with more time on every stitch. For precious or sentimental pieces."}
                    </span>
                    {name === "Premium" && (
                      <em>
                        Required for very fine or intricately stitched sweaters,
                        like most cashmere and merino.
                      </em>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="sweater-block">
              <h3 className="sweater-h">Holes</h3>
              <p className="sweater-note">
                Measure each hole at its widest point.
              </p>
              {isLoading && <p className="sweater-note">Loading prices…</p>}
              {holes.map((hole, index) => (
                <div className="sweater-hole" key={index}>
                  <span className="sweater-hole-n">Hole {index + 1}</span>
                  <select
                    id={`hole-size-${index}`}
                    aria-label={`Size of hole ${index + 1}`}
                    value={hole.size}
                    onChange={(e) => setHole(index, { size: +e.target.value })}
                  >
                    {holeOptions.map((service, i) => (
                      <option key={service._id} value={i}>
                        {holeSizeLabel(service)}
                      </option>
                    ))}
                  </select>
                  <span className="sweater-price">
                    {charge === PER_HOLE && holeOptions[hole.size]
                      ? `$${rowPrices[index]}`
                      : ""}
                  </span>
                  <button
                    type="button"
                    className="sweater-remove"
                    aria-label={`Remove hole ${index + 1}`}
                    onClick={() =>
                      setHoles((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </button>
                  <select
                    id={`hole-place-${index}`}
                    aria-label={`Where hole ${index + 1} is`}
                    className={
                      hole.place
                        ? "sweater-where"
                        : tried
                          ? "sweater-where empty sweater-gap"
                          : "sweater-where empty"
                    }
                    value={hole.place}
                    onChange={(e) => setHole(index, { place: e.target.value })}
                  >
                    <option value="">Where is it?</option>
                    {HOLE_PLACES.map((place) => (
                      <option key={place}>{place}</option>
                    ))}
                    <option>{SOMEWHERE_ELSE}</option>
                  </select>
                  {hole.place === SOMEWHERE_ELSE && (
                    <input
                      id={`hole-other-${index}`}
                      type="text"
                      className={
                        tried && !hole.other.trim()
                          ? "sweater-where sweater-other sweater-gap"
                          : "sweater-where sweater-other"
                      }
                      maxLength={80}
                      placeholder="Where is it? e.g. left pocket flap"
                      aria-label={`Describe where hole ${index + 1} is`}
                      value={hole.other}
                      onChange={(e) =>
                        setHole(index, { other: e.target.value })
                      }
                    />
                  )}
                  <div
                    id={`hole-method-${index}`}
                    className={
                      tried && !hole.method
                        ? "sweater-method sweater-gap"
                        : "sweater-method"
                    }
                    role="radiogroup"
                    aria-label={`How to fix hole ${index + 1}`}
                  >
                    {METHODS.map((method) => (
                      <button
                        key={method}
                        type="button"
                        role="radio"
                        aria-checked={hole.method === method}
                        className={hole.method === method ? "on" : ""}
                        onClick={() => setHole(index, { method })}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                  {hole.method === REKNIT && (
                    <p className="sweater-method-note" role="status">
                      {REKNIT_REMINDER}
                    </p>
                  )}
                </div>
              ))}
              {holes.length < MAX_HOLES && (
                <button
                  type="button"
                  className="sweater-add"
                  onClick={() => setHoles((prev) => [...prev, newHole()])}
                >
                  {holes.length ? "+ Add another hole" : "+ Add a hole"}
                </button>
              )}
              {chosenHoles.length > 0 && !showCharge && (
                <div>
                  <button
                    type="button"
                    className="sweater-switch"
                    onClick={() => setChoosing(true)}
                  >
                    Charge as restoration instead
                  </button>
                </div>
              )}
            </div>

            {chosenHoles.length > 0 && showCharge && (
              <div className="sweater-block">
                <h3 className="sweater-h">How are we charging?</h3>
                <div className="sweater-charge" role="radiogroup">
                  <label>
                    <input
                      type="radio"
                      name="charge"
                      checked={charge === PER_HOLE}
                      onChange={() => setCharge(PER_HOLE)}
                    />
                    <span>
                      Per hole: ${perHoleTotal}
                      <small>
                        {chosenHoles.length === 1
                          ? "1 hole at its size's price."
                          : `${chosenHoles.length} holes, each at its size's price.`}
                      </small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="charge"
                      checked={charge === RESTORATION}
                      onChange={() => setCharge(RESTORATION)}
                    />
                    <span>
                      Restoration: one price
                      <small>
                        Quote it on the Prices tab, then type the price here.
                      </small>
                    </span>
                  </label>
                  {charge === RESTORATION && (
                    <div className="sweater-restore-price">
                      <span>$</span>
                      <input
                        id="restoration-price"
                        type="number"
                        min="0"
                        step="1"
                        inputMode="decimal"
                        placeholder="e.g. 240"
                        aria-label="Restoration price"
                        className={
                          tried && !parseFloat(restorationPrice)
                            ? "sweater-gap"
                            : ""
                        }
                        value={restorationPrice}
                        onChange={(e) => setRestorationPrice(e.target.value)}
                      />
                    </div>
                  )}
                  <label>
                    <input
                      type="radio"
                      name="charge"
                      checked={charge === CONFIRM_LATER}
                      onChange={() => setCharge(CONFIRM_LATER)}
                    />
                    <span>
                      Price to confirm later: charge one hole now ($
                      {priceOn(chosenHoles[0].service, plan)})
                      <small>
                        Ask {whoEstimates(chosenHoles)} for a time estimate,
                        then invoice the rest with Add extra work. A note goes
                        on the order.
                      </small>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {extraOptions.length > 0 && (
              <div className="sweater-block">
                <h3 className="sweater-h">Anything else?</h3>
                {extraOptions.map((service) => {
                  const n = counts[service._id] || 0;
                  return (
                    <div className="sweater-extra" key={service._id}>
                      <label htmlFor={`extra-${service._id}`}>
                        <input
                          id={`extra-${service._id}`}
                          type="checkbox"
                          checked={n > 0}
                          onChange={(e) =>
                            setCount(service._id, e.target.checked ? 1 : 0)
                          }
                        />
                        {extraLabel(service)}
                      </label>
                      <span className="sweater-extra-right">
                        {isCountable(service) && n > 0 && (
                          <span className="sweater-count">
                            <button
                              type="button"
                              aria-label={`One fewer: ${extraLabel(service)}`}
                              onClick={() => setCount(service._id, n - 1)}
                            >
                              −
                            </button>
                            <span aria-live="polite">{n}</span>
                            <button
                              type="button"
                              aria-label={`One more: ${extraLabel(service)}`}
                              disabled={n >= MAX_COUNT}
                              onClick={() => setCount(service._id, n + 1)}
                            >
                              +
                            </button>
                          </span>
                        )}
                        <span className="sweater-price">
                          ${priceOn(service, plan) * Math.max(1, n)}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sweater-block">
              <h3 className="sweater-h">Custom work</h3>
              <p className="sweater-note">
                Not on the list, charged by time, or an alteration. Say what we
                are doing and type the price.
              </p>
              {customs.map((custom, index) => (
                <div className="sweater-hole sweater-custom" key={index}>
                  <span className="sweater-hole-n">Custom {index + 1}</span>
                  <input
                    id={`custom-what-${index}`}
                    type="text"
                    maxLength={120}
                    placeholder={
                      custom.kind === "alteration"
                        ? "e.g. Shorten sleeves 2in"
                        : "e.g. Rebuild the cuff, 1.5 hours"
                    }
                    aria-label={`What custom work ${index + 1} is`}
                    className={
                      tried && custom.price && !custom.what.trim()
                        ? "sweater-gap"
                        : ""
                    }
                    value={custom.what}
                    onChange={(e) => setCustom(index, { what: e.target.value })}
                  />
                  <span className="sweater-custom-price">
                    <span>$</span>
                    <input
                      id={`custom-price-${index}`}
                      type="number"
                      min="0"
                      step="1"
                      inputMode="decimal"
                      placeholder="0"
                      aria-label={`Price of custom work ${index + 1}`}
                      className={
                        tried && custom.what.trim() && !parseFloat(custom.price)
                          ? "sweater-gap"
                          : ""
                      }
                      value={custom.price}
                      onChange={(e) =>
                        setCustom(index, { price: e.target.value })
                      }
                    />
                  </span>
                  <button
                    type="button"
                    className="sweater-remove"
                    aria-label={`Remove custom work ${index + 1}`}
                    onClick={() =>
                      setCustoms((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </button>
                  <div
                    className="sweater-method"
                    role="radiogroup"
                    aria-label={`Repair or alteration, custom work ${index + 1}`}
                  >
                    {CUSTOM_KINDS.map(({ kind, label }) => (
                      <button
                        key={kind}
                        type="button"
                        role="radio"
                        aria-checked={custom.kind === kind}
                        className={custom.kind === kind ? "on" : ""}
                        onClick={() => setCustom(index, { kind })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {customs.length < MAX_CUSTOM && (
                <button
                  type="button"
                  className="sweater-add"
                  onClick={() => setCustoms((prev) => [...prev, newCustom()])}
                >
                  {chosenCustoms(customs).length
                    ? "+ Add more custom work"
                    : "+ Add custom work"}
                </button>
              )}
            </div>

            <div className="sweater-block">
              <h3 className="sweater-h">
                <label htmlFor="sweater-notes">
                  Anything else for our fixers?
                </label>{" "}
                <span className="item-field-optional">(optional)</span>
              </h3>
              <textarea
                id="sweater-notes"
                className="sweater-notes"
                placeholder="e.g. Make the repair visible in red, or be careful of the beaded trim."
                value={notes}
                maxLength={MAX_NOTE}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="sweater-block">
              <div className="sweater-total">
                <span>Total</span>
                <span>${total}</span>
              </div>
            </div>

            <button
              className="section-main-btn sweater-continue"
              type="button"
              onClick={handleContinue}
            >
              Continue{" "}
              <span>
                <img src={btnArrow} alt="" />
              </span>
            </button>
            {tried && missing.length > 0 && (
              <p className="sweater-missing" role="alert">
                To continue, {missing.join(" and ")}.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SweaterScreen;
